import { create } from 'zustand';
import type { Product } from '@wconcept/wconcept-common/models/renewal/product';

export interface ModuleProductData {
  moduleType: string;
  contentId: string;
  itemCodes: string[];
  products: Product[];
  itemStatusCd?: Array<'01' | '02' | '04' | '05'>;
}

export interface QueryStatus {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  error: unknown;
  productsMap?: Map<string, Product>; // itemCd를 key로 하는 Map 구조
}

interface PromotionVisibilityState {
  accumulatedModuleData: Map<string, ModuleProductData>;
  accumulatedQueryStatus: Map<string, QueryStatus>;
  contentsKey: string | null;
  setContentsKey: (key: string) => void;
  reset: () => void;
  mergeModuleData: (
    updates: Map<string, ModuleProductData> | Record<string, ModuleProductData>
  ) => void;
  mergeQueryStatus: (
    updates: Map<string, QueryStatus> | Record<string, QueryStatus>
  ) => void;
}

const usePromotionVisibilityStore = create<PromotionVisibilityState>(
  (set, get) => ({
    accumulatedModuleData: new Map<string, ModuleProductData>(),
    accumulatedQueryStatus: new Map<string, QueryStatus>(),
    contentsKey: null,
    setContentsKey: (key: string) => {
      const prevKey = get().contentsKey;
      if (prevKey !== key) {
        set({
          contentsKey: key,
          accumulatedModuleData: new Map<string, ModuleProductData>(),
          accumulatedQueryStatus: new Map<string, QueryStatus>(),
        });
      }
    },
    reset: () =>
      set({
        accumulatedModuleData: new Map<string, ModuleProductData>(),
        accumulatedQueryStatus: new Map<string, QueryStatus>(),
        contentsKey: null,
      }),
    mergeModuleData: updates => {
      const current = get().accumulatedModuleData;
      const next = new Map(current);
      if (updates instanceof Map) {
        updates.forEach((value, key) => {
          next.set(key, value);
        });
      } else {
        Object.entries(updates).forEach(([key, value]) => {
          next.set(key, value);
        });
      }
      // 변경사항이 있을 때만 상태 업데이트
      if (
        next.size !== current.size ||
        Array.from(next.keys()).some(key => next.get(key) !== current.get(key))
      ) {
        set({ accumulatedModuleData: next });
      }
    },
    mergeQueryStatus: updates => {
      const current = get().accumulatedQueryStatus;
      const next = new Map(current);
      if (updates instanceof Map) {
        updates.forEach((value, key) => {
          next.set(key, value);
        });
      } else {
        Object.entries(updates).forEach(([key, value]) => {
          next.set(key, value);
        });
      }
      // 변경사항이 있을 때만 상태 업데이트
      if (
        next.size !== current.size ||
        Array.from(next.keys()).some(key => next.get(key) !== current.get(key))
      ) {
        set({ accumulatedQueryStatus: next });
      }
    },
  })
);

export default usePromotionVisibilityStore;
